import { useRef, useState } from 'react';
import { Heart, Plus } from 'lucide-react';
import type {
  ProArticle,
  ProArticlePricing,
  RemiseTier,
} from '@erp/domain/pro';
import {
  calculateProLineTotals,
  resolveTierLinePricing,
} from '@erp/domain/pro';
import { formatTND } from '@/lib/format';
import { useFlyToCart } from '@/lib/fly-to-cart';
import { useClientMode } from '@/lib/client-mode';

export interface ProductCardProps {
  article: ProArticle;
  pricing: ProArticlePricing | null;
  tier: RemiseTier;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  onAddToCart: () => void;
  onClick: () => void;
}

export function ProductCard({
  article,
  pricing,
  tier,
  isFavorite,
  onToggleFavorite,
  onAddToCart,
  onClick,
}: ProductCardProps) {
  const linePricing = resolveTierLinePricing(article, pricing, tier);
  const unitTtc = calculateProLineTotals({
    quantity: 1,
    unitHt: linePricing.unitHt,
    remisePct: linePricing.remisePct,
    vatRate: article.vatRate,
  }).pttc;
  const imageRef = useRef<HTMLDivElement>(null);
  const { flyToCart } = useFlyToCart();
  const [favoriteBump, setFavoriteBump] = useState(0);
  // Les favoris sont un outil de l'électricien, pas une info pour son client.
  const clientMode = useClientMode();

  const handleAdd = () => {
    if (imageRef.current) {
      flyToCart(imageRef.current, article.imageUrl ?? null);
    }
    onAddToCart();
  };

  const handleFavorite = () => {
    setFavoriteBump((k) => k + 1);
    onToggleFavorite();
  };

  return (
    <article className="group bg-white rounded-2xl border border-neutral-200 hover:border-neutral-300 transition overflow-hidden flex flex-col pro-hover-lift">
      <button
        type="button"
        onClick={onClick}
        className="aspect-square w-full relative bg-neutral-50 overflow-hidden"
      >
        <div
          ref={imageRef}
          className="absolute inset-0 flex items-center justify-center"
        >
          {article.imageUrl ? (
            <img
              src={article.imageUrl}
              alt={article.name}
              className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-300 text-4xl">
              ⚡
            </div>
          )}
        </div>
      </button>

      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <div className="text-xs text-neutral-500">{article.code}</div>
        <button
          type="button"
          onClick={onClick}
          className="text-sm font-medium text-neutral-900 line-clamp-2 text-left hover:text-orange-600"
        >
          {article.name}
        </button>
        <div className="text-base font-semibold text-neutral-900 mt-auto">
          {formatTND(unitTtc)}{' '}
          <span className="text-xs font-normal text-neutral-500">TTC</span>
        </div>
      </div>

      <div className="px-3 pb-3 flex items-center gap-2 min-w-0">
        {!clientMode && (
          <button
            type="button"
            aria-label={
              isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'
            }
            onClick={handleFavorite}
            className={`shrink-0 p-2 rounded-lg border ${
              isFavorite
                ? 'border-red-200 bg-red-50 text-red-500'
                : 'border-neutral-200 text-neutral-400 hover:text-red-500'
            }`}
          >
            <Heart
              key={favoriteBump}
              className={`w-4 h-4 ${favoriteBump ? 'pro-pop' : ''}`}
              fill={isFavorite ? 'currentColor' : 'none'}
            />
          </button>
        )}
        <button
          type="button"
          onClick={handleAdd}
          className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium text-white"
          style={{ background: 'var(--pro-primary)' }}
        >
          <Plus className="w-4 h-4 shrink-0" />
          <span className="truncate">Ajouter</span>
        </button>
      </div>
    </article>
  );
}
